import {
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type FocusEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react';

import { deleteOneCharacter, formatWithCaret, replaceAll, type MaskModel, type MaskedText } from './caret-mask';

// Vendorizado de UY3.Front/packages/ui/src/internal/useMaskedInput.ts (U03), genérico no tipo do
// valor: o chamador decide o que o `onChange` recebe (cru, centavos, fração, ISO).
export interface MaskedInputOptions<V> {
  readonly model: MaskModel;
  readonly toValue: (significant: string) => V;
  readonly fromValue: (value: V) => string;
  /** Igualdade de valor, não `===`: evita reconstruir o texto sob o cursor quando o pai normaliza. */
  readonly sameValue: (left: V, right: V) => boolean;
  readonly value: V;
  readonly onChange?: ((value: V) => void) | undefined;
  readonly onBlur?: ((event: FocusEvent<HTMLInputElement>) => void) | undefined;
  /** Lê um colar inteiro em caracteres significativos; `null` deixa o navegador inserir. */
  readonly onPasteText?: ((pasted: string) => string | null) | undefined;
  /** Assenta o valor no blur (`R$ 1.234,` → `R$ 1.234,00`). */
  readonly settle?: ((significant: string) => string) | undefined;
}

export interface MaskedInputBindings {
  readonly ref: RefObject<HTMLInputElement | null>;
  readonly value: string;
  readonly onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  readonly onPaste: (event: ClipboardEvent<HTMLInputElement>) => void;
  readonly onBlur: (event: FocusEvent<HTMLInputElement>) => void;
}

interface State {
  readonly text: string;
  readonly caret: number | null;
  readonly tick: number;
}

export function useMaskedInput<V>(options: MaskedInputOptions<V>): MaskedInputBindings {
  const { model, toValue, fromValue, sameValue, value, onChange, onBlur, onPasteText, settle } = options;
  const ref = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<State>(() => ({ text: model.render(fromValue(value)), caret: null, tick: 0 }));
  const [seenValue, setSeenValue] = useState(value);

  // Estado ajustado no render (padrão do React para "prop mudou"): um efeito mostraria o texto
  // velho por um quadro, e num campo de dinheiro isso é um valor errado na tela.
  if (!sameValue(value, seenValue)) {
    setSeenValue(value);
    if (!sameValue(value, toValue(model.significant(state.text)))) {
      setState((current) => ({ text: model.render(fromValue(value)), caret: null, tick: current.tick + 1 }));
    }
  }

  useLayoutEffect(() => {
    const input = ref.current;
    if (input === null || state.caret === null || document.activeElement !== input) return;
    input.setSelectionRange(state.caret, state.caret);
  }, [state]);

  const commit = (next: MaskedText) => {
    setState((current) => ({ text: next.value, caret: next.caret, tick: current.tick + 1 }));
    onChange?.(toValue(model.significant(next.value)));
  };

  return {
    ref,
    value: state.text,
    onChange: (event) => {
      const input = event.target;
      commit(formatWithCaret(model, input.value, input.selectionStart ?? input.value.length));
    },
    onKeyDown: (event) => {
      if (event.key !== 'Backspace' && event.key !== 'Delete') return;
      const input = event.currentTarget;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      if (start === null || end === null || start !== end) return;
      event.preventDefault();
      const next = deleteOneCharacter(model, input.value, start, event.key === 'Backspace' ? 'backward' : 'forward');
      if (next !== null) commit(next);
    },
    onPaste: (event) => {
      if (onPasteText === undefined) return;
      const significant = onPasteText(event.clipboardData.getData('text'));
      if (significant === null) return;
      event.preventDefault();
      commit(replaceAll(model, significant));
    },
    onBlur: (event) => {
      if (settle !== undefined) {
        const significant = model.significant(state.text);
        const settled = settle(significant);
        if (settled !== significant) commit(replaceAll(model, settled));
      }
      onBlur?.(event);
    },
  };
}
